import { useState } from 'react';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  RotateCcw,
  Search,
  ShieldCheck,
  ShieldX,
} from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  PageHeader,
  Skeleton,
  StatCard,
  Table,
  Td,
  Th,
} from '@/components/ui';
import { ApiError } from '@/lib/api-client';
import { useAuth } from '@/lib/auth-context';
import {
  openDocument,
  useCustomerAction,
  useCustomerDetail,
  useCustomerQueue,
  useQueueSummary,
  useReviewDocument,
  type CustomerStatus,
  type VerificationDocument,
} from '@/lib/customers-api';
import { humanize, statusTone } from '@/lib/status';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

const TABS = [
  { label: 'Awaiting review', statuses: ['VERIFICATION_PENDING', 'UNDER_REVIEW'] },
  { label: 'Submitted', statuses: ['VERIFICATION_PENDING'] },
  { label: 'Under review', statuses: ['UNDER_REVIEW'] },
  { label: 'Verified', statuses: ['VERIFIED'] },
  { label: 'Rejected', statuses: ['REJECTED'] },
  { label: 'Suspended', statuses: ['SUSPENDED', 'BLOCKED'] },
  { label: 'All', statuses: [] },
] as const satisfies ReadonlyArray<{ label: string; statuses: readonly CustomerStatus[] }>;

type TabLabel = (typeof TABS)[number]['label'];

const PAGE_SIZE = 20;

const DOCUMENT_TONE: Record<string, 'success' | 'danger' | 'warning'> = {
  APPROVED: 'success',
  REJECTED: 'danger',
  PENDING: 'warning',
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatFileSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * KYC verification queue and review workspace (spec §31).
 *
 * The screen is organised around the decision an admin is actually making:
 * the queue narrows to what needs attention, and the drawer puts the evidence
 * (documents), the identity claims (GST/PAN) and the decision buttons in one
 * place so nobody approves a business from a list row alone.
 */
export function CustomerVerificationPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<TabLabel>('Awaiting review');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const activeTab = TABS.find((entry) => entry.label === tab) ?? TABS[0];

  const queue = useCustomerQueue({
    page,
    limit: PAGE_SIZE,
    search,
    status: [...activeTab.statuses],
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  });

  const summary = useQueueSummary();

  const changeTab = (next: TabLabel) => {
    setTab(next);
    setPage(1);
  };

  const counts = summary.data?.byStatus ?? {};

  return (
    <>
      <PageHeader
        title="KYC Verification"
        description="Review business documents and decide who may trade"
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Awaiting review"
          value={summary.isLoading ? '—' : formatNumber(summary.data?.awaitingReview ?? 0)}
          tone="warning"
          icon={<Clock className="size-5" aria-hidden="true" />}
        />
        <StatCard
          label="Documents pending"
          value={summary.isLoading ? '—' : formatNumber(summary.data?.pendingDocuments ?? 0)}
          tone="brand"
          icon={<FileText className="size-5" aria-hidden="true" />}
        />
        <StatCard
          label="Verified"
          value={summary.isLoading ? '—' : formatNumber(counts.VERIFIED ?? 0)}
          tone="success"
          icon={<CheckCircle2 className="size-5" aria-hidden="true" />}
        />
        <StatCard
          label="Rejected / blocked"
          value={
            summary.isLoading
              ? '—'
              : formatNumber((counts.REJECTED ?? 0) + (counts.BLOCKED ?? 0))
          }
          tone="danger"
          icon={<Ban className="size-5" aria-hidden="true" />}
        />
      </div>

      <Card className="mt-5">
        <div className="flex gap-1 overflow-x-auto border-b border-line px-4">
          {TABS.map((entry) => (
            <button
              key={entry.label}
              type="button"
              onClick={() => changeTab(entry.label)}
              aria-pressed={tab === entry.label}
              className={cn(
                'whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                tab === entry.label
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-ink-muted hover:text-ink',
              )}
            >
              {entry.label}
            </button>
          ))}
        </div>

        <div className="border-b border-line p-4">
          <div className="relative max-w-md">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            />
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search by company, code, GSTIN, email or phone…"
              aria-label="Search customers awaiting verification"
              className="w-full rounded-lg border border-line py-2 pl-9 pr-3 text-sm placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none"
            />
          </div>
        </div>

        {queue.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="h-14" />
            ))}
          </div>
        ) : queue.isError ? (
          <div className="p-4">
            <EmptyState
              title="Could not load the verification queue"
              description={
                queue.error instanceof ApiError ? queue.error.message : 'Please try again.'
              }
            />
          </div>
        ) : queue.data && queue.data.items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              title="Nothing here"
              description={
                tab === 'Awaiting review'
                  ? 'Every submitted application has been dealt with.'
                  : 'No customers match this filter.'
              }
            />
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Business</Th>
                <Th>Contact</Th>
                <Th>GSTIN</Th>
                <Th className="text-right">Documents</Th>
                <Th>Status</Th>
                <Th>Gates</Th>
                <Th>Registered</Th>
              </tr>
            </thead>
            <tbody>
              {queue.data?.items.map((customer) => (
                <tr
                  key={customer.id}
                  onClick={() => setSelectedId(customer.id)}
                  className="cursor-pointer hover:bg-canvas"
                >
                  <Td>
                    <p className="font-medium text-ink">{customer.companyName}</p>
                    <p className="text-xs text-ink-subtle">
                      {customer.customerCode} · {humanize(customer.businessType)}
                    </p>
                  </Td>
                  <Td>
                    <p className="truncate text-ink">{customer.contactName ?? '—'}</p>
                    <p className="truncate text-xs text-ink-subtle">{customer.contactEmail}</p>
                  </Td>
                  <Td className="font-mono text-xs text-ink-muted">{customer.gstNumber ?? '—'}</Td>
                  <Td className="text-right text-ink">{customer.documentCount}</Td>
                  <Td>
                    <Badge tone={statusTone(customer.status)}>{humanize(customer.status)}</Badge>
                  </Td>
                  <Td>
                    <div className="flex gap-1">
                      <Badge tone={customer.isVerified ? 'success' : 'neutral'}>KYC</Badge>
                      <Badge tone={customer.canOrder ? 'success' : 'neutral'}>Order</Badge>
                    </div>
                  </Td>
                  <Td className="whitespace-nowrap text-ink-subtle">
                    {formatDate(customer.createdAt)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {queue.data && queue.data.pagination.total > 0 && (
          <div className="flex items-center justify-between gap-4 border-t border-line px-4 py-3 text-sm text-ink-subtle">
            <span>
              Page {queue.data.pagination.page} of {queue.data.pagination.totalPages} ·{' '}
              {formatNumber(queue.data.pagination.total)} customers
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!queue.data.pagination.hasPreviousPage}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!queue.data.pagination.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ReviewDrawer
        customerId={selectedId}
        onClose={() => setSelectedId(null)}
        canVerify={can('customer.verify')}
        canApprove={can('customer.approve')}
        canSuspend={can('customer.suspend')}
      />
    </>
  );
}

/* ==========================================================================
   Review drawer
   ========================================================================== */

type PromptKind = 'reject' | 'request-documents' | 'suspend' | null;

function ReviewDrawer({
  customerId,
  onClose,
  canVerify,
  canApprove,
  canSuspend,
}: {
  customerId: string | null;
  onClose: () => void;
  canVerify: boolean;
  canApprove: boolean;
  canSuspend: boolean;
}) {
  const detail = useCustomerDetail(customerId);
  const action = useCustomerAction(customerId);
  const reviewDocument = useReviewDocument(customerId);

  const [prompt, setPrompt] = useState<PromptKind>(null);
  const [reason, setReason] = useState('');
  const [enableOrdering, setEnableOrdering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const customer = detail.data;
  const busy = action.isPending || reviewDocument.isPending;

  const close = () => {
    setPrompt(null);
    setReason('');
    setEnableOrdering(false);
    setError(null);
    onClose();
  };

  const run = (input: Parameters<typeof action.mutate>[0]) => {
    setError(null);
    action.mutate(input, {
      onSuccess: () => {
        setPrompt(null);
        setReason('');
      },
      onError: (cause) =>
        setError(cause instanceof ApiError ? cause.message : 'The action could not be completed.'),
    });
  };

  const submitPrompt = () => {
    if (!reason.trim()) {
      setError('Please give a reason — the customer sees it.');
      return;
    }

    if (prompt === 'reject') run({ action: 'reject', body: { reason } });
    if (prompt === 'request-documents') run({ action: 'request-documents', body: { reason } });
    if (prompt === 'suspend') run({ action: 'suspend', body: { reason } });
  };

  const decideDocument = (document_: VerificationDocument, decision: 'APPROVE' | 'REJECT') => {
    if (decision === 'REJECT') {
      const input = window.prompt(`Why is "${document_.fileName}" being rejected?`);
      if (!input?.trim()) return;

      reviewDocument.mutate({ documentId: document_.id, decision, rejectionReason: input.trim() });
      return;
    }

    reviewDocument.mutate({ documentId: document_.id, decision });
  };

  const awaitingDecision =
    customer?.status === 'VERIFICATION_PENDING' || customer?.status === 'UNDER_REVIEW';

  return (
    <Drawer
      open={customerId !== null}
      onClose={close}
      title="Verification review"
      footer={
        customer && (
          <div className="space-y-2">
            {error && (
              <p role="alert" className="rounded-lg bg-danger-50 px-3 py-2 text-xs text-danger-700">
                {error}
              </p>
            )}

            {prompt ? (
              <div className="space-y-2">
                <label htmlFor="reason" className="block text-xs font-medium text-ink">
                  {prompt === 'reject'
                    ? 'Reason for rejection (shown to the customer)'
                    : prompt === 'request-documents'
                      ? 'What else do you need from them?'
                      : 'Reason for suspension'}
                </label>
                <textarea
                  id="reason"
                  rows={3}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  className="w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => setPrompt(null)} disabled={busy}>
                    Cancel
                  </Button>
                  <Button
                    variant={prompt === 'request-documents' ? 'primary' : 'danger'}
                    onClick={submitPrompt}
                    disabled={busy}
                  >
                    {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
                    Confirm
                  </Button>
                </div>
              </div>
            ) : (
              <VerificationActions
                status={customer.status}
                canOrder={customer.canOrder}
                isVerified={customer.isVerified}
                awaitingDecision={awaitingDecision}
                busy={busy}
                canVerify={canVerify}
                canApprove={canApprove}
                canSuspend={canSuspend}
                enableOrdering={enableOrdering}
                onToggleOrdering={setEnableOrdering}
                onStartReview={() => run({ action: 'review' })}
                onApprove={() => run({ action: 'approve', body: { enableOrdering } })}
                onReject={() => setPrompt('reject')}
                onRequestDocuments={() => setPrompt('request-documents')}
                onSuspend={() => setPrompt('suspend')}
                onReinstate={() => run({ action: 'reinstate' })}
                onSetOrdering={(next) =>
                  run({ action: 'ordering', method: 'PATCH', body: { canOrder: next } })
                }
              />
            )}
          </div>
        )
      }
    >
      {detail.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-16" />
          ))}
        </div>
      ) : detail.isError ? (
        <EmptyState
          title="Could not load this customer"
          description={detail.error instanceof ApiError ? detail.error.message : undefined}
        />
      ) : customer ? (
        <div className="space-y-6">
          <header>
            <p className="font-semibold text-ink">{customer.companyName}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <Badge tone={statusTone(customer.status)}>{humanize(customer.status)}</Badge>
              <span className="text-xs text-ink-subtle">{customer.customerCode}</span>
            </div>
          </header>

          {/* The three ordering gates, spelled out — being verified is not the
              same as being allowed to place orders (spec §5, §79). */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink">Ordering gates</h3>
            <ul className="space-y-1.5 text-sm">
              <GateRow label="Business verified" ok={customer.isVerified} />
              <GateRow label="Ordering enabled" ok={customer.canOrder} />
              <GateRow
                label="Security deposit"
                ok={
                  customer.securityDeposit !== null &&
                  customer.securityDeposit.outstandingAmount <= 0
                }
                detail={
                  customer.securityDeposit
                    ? customer.securityDeposit.outstandingAmount > 0
                      ? `${formatCurrency(customer.securityDeposit.outstandingAmount)} outstanding`
                      : `${formatCurrency(customer.securityDeposit.paidAmount)} paid`
                    : 'No deposit record'
                }
              />
            </ul>
          </section>

          {customer.rejectionReason && (
            <p className="flex gap-2 rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>{customer.rejectionReason}</span>
            </p>
          )}

          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink">Business identity</h3>
            <dl className="space-y-2 text-sm">
              {(
                [
                  ['Business type', humanize(customer.businessType)],
                  ['GSTIN', customer.gstNumber ?? '—'],
                  ['PAN', customer.panNumber ?? '—'],
                  ['Registration no.', customer.businessRegistrationNumber ?? '—'],
                  ['Years in business', customer.yearsInBusiness?.toString() ?? '—'],
                  [
                    'Expected monthly volume',
                    customer.expectedMonthlyVolume
                      ? formatCurrency(customer.expectedMonthlyVolume)
                      : '—',
                  ],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4 border-b border-line pb-2">
                  <dt className="text-ink-subtle">{label}</dt>
                  <dd className="text-right font-medium text-ink">{value}</dd>
                </div>
              ))}
            </dl>
          </section>

          {customer.contact && (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">Contact</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-4 border-b border-line pb-2">
                  <dt className="text-ink-subtle">Name</dt>
                  <dd className="font-medium text-ink">
                    {customer.contact.firstName} {customer.contact.lastName}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-4 border-b border-line pb-2">
                  <dt className="text-ink-subtle">Email</dt>
                  <dd className="flex items-center gap-2 font-medium text-ink">
                    {customer.contact.email}
                    <Badge tone={customer.contact.emailVerified ? 'success' : 'warning'}>
                      {customer.contact.emailVerified ? 'Verified' : 'Unverified'}
                    </Badge>
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-4 border-b border-line pb-2">
                  <dt className="text-ink-subtle">Mobile</dt>
                  <dd className="flex items-center gap-2 font-medium text-ink">
                    {customer.contact.mobile ?? '—'}
                    <Badge tone={customer.contact.mobileVerified ? 'success' : 'warning'}>
                      {customer.contact.mobileVerified ? 'Verified' : 'Unverified'}
                    </Badge>
                  </dd>
                </div>
              </dl>
            </section>
          )}

          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink">
              Documents{' '}
              <span className="font-normal text-ink-subtle">({customer.documents.length})</span>
            </h3>

            {customer.documents.length === 0 ? (
              <p className="rounded-lg border border-dashed border-line px-3 py-6 text-center text-sm text-ink-subtle">
                Nothing uploaded yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {customer.documents.map((document_) => (
                  <li key={document_.id} className="rounded-lg border border-line p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">
                          {humanize(document_.type)}
                        </p>
                        <p className="truncate text-xs text-ink-subtle">
                          {document_.fileName} · {formatFileSize(document_.fileSizeBytes)}
                        </p>
                        {document_.documentNumber && (
                          <p className="mt-0.5 font-mono text-xs text-ink-muted">
                            {document_.documentNumber}
                          </p>
                        )}
                      </div>
                      <Badge tone={DOCUMENT_TONE[document_.status] ?? 'neutral'}>
                        {humanize(document_.status)}
                      </Badge>
                    </div>

                    {document_.rejectionReason && (
                      <p className="mt-2 rounded bg-danger-50 px-2 py-1 text-xs text-danger-700">
                        {document_.rejectionReason}
                      </p>
                    )}

                    <div className="mt-2.5 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          void openDocument(document_).catch(() =>
                            setError('The document could not be opened.'),
                          );
                        }}
                      >
                        <ExternalLink className="size-3.5" aria-hidden="true" />
                        Open
                      </Button>

                      {canVerify && document_.status !== 'APPROVED' && (
                        <Button
                          size="sm"
                          variant="success"
                          disabled={busy}
                          onClick={() => decideDocument(document_, 'APPROVE')}
                        >
                          Approve
                        </Button>
                      )}

                      {canVerify && document_.status !== 'REJECTED' && (
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={busy}
                          onClick={() => decideDocument(document_, 'REJECT')}
                        >
                          Reject
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {customer.addresses.length > 0 && (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">Addresses</h3>
              <ul className="space-y-2 text-sm">
                {customer.addresses.map((address) => (
                  <li key={address.id} className="rounded-lg border border-line p-3">
                    <p className="flex items-center gap-2 font-medium text-ink">
                      {address.label}
                      {address.isDefault && <Badge tone="brand">Default</Badge>}
                    </p>
                    <p className="mt-1 text-xs text-ink-muted">
                      {address.addressLine1}
                      {address.addressLine2 ? `, ${address.addressLine2}` : ''}, {address.city},{' '}
                      {address.state} {address.pincode}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink">Verification history</h3>
            <ol className="space-y-3">
              {customer.verificationHistory.map((entry) => (
                <li key={entry.id} className="border-l-2 border-line pl-3">
                  <p className="text-sm font-medium text-ink">{humanize(entry.action)}</p>
                  <p className="text-xs text-ink-subtle">
                    {entry.previousStatus ? `${humanize(entry.previousStatus)} → ` : ''}
                    {humanize(entry.newStatus)} · {formatDate(entry.createdAt)}
                    {entry.performedBy ? ` · ${entry.performedBy}` : ''}
                  </p>
                  {entry.notes && <p className="mt-1 text-xs text-ink-muted">{entry.notes}</p>}
                </li>
              ))}
            </ol>
          </section>
        </div>
      ) : null}
    </Drawer>
  );
}

function GateRow({ label, ok, detail }: { label: string; ok: boolean; detail?: string }) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-lg bg-canvas px-3 py-2">
      <span className="text-ink-muted">{label}</span>
      <span className="flex items-center gap-2">
        {detail && <span className="text-xs text-ink-subtle">{detail}</span>}
        <Badge tone={ok ? 'success' : 'danger'}>{ok ? 'Met' : 'Not met'}</Badge>
      </span>
    </li>
  );
}

/**
 * The decision buttons.
 *
 * Which ones appear is driven by the same state machine the server enforces, so
 * an admin is not offered an action that would come back as a 409.
 */
function VerificationActions({
  status,
  canOrder,
  isVerified,
  awaitingDecision,
  busy,
  canVerify,
  canApprove,
  canSuspend,
  enableOrdering,
  onToggleOrdering,
  onStartReview,
  onApprove,
  onReject,
  onRequestDocuments,
  onSuspend,
  onReinstate,
  onSetOrdering,
}: {
  status: CustomerStatus;
  canOrder: boolean;
  isVerified: boolean;
  awaitingDecision: boolean;
  busy: boolean;
  canVerify: boolean;
  canApprove: boolean;
  canSuspend: boolean;
  enableOrdering: boolean;
  onToggleOrdering: (next: boolean) => void;
  onStartReview: () => void;
  onApprove: () => void;
  onReject: () => void;
  onRequestDocuments: () => void;
  onSuspend: () => void;
  onReinstate: () => void;
  onSetOrdering: (next: boolean) => void;
}) {
  const suspended = status === 'SUSPENDED' || status === 'BLOCKED';

  return (
    <div className="space-y-2">
      {status === 'VERIFICATION_PENDING' && canVerify && (
        <Button fullWidth variant="outline" onClick={onStartReview} disabled={busy}>
          <Clock className="size-4" aria-hidden="true" />
          Take under review
        </Button>
      )}

      {awaitingDecision && canApprove && (
        <label className="flex items-center gap-2 rounded-lg bg-canvas px-3 py-2 text-xs text-ink-muted">
          <input
            type="checkbox"
            checked={enableOrdering}
            onChange={(event) => onToggleOrdering(event.target.checked)}
            className="size-4 rounded border-line text-brand-600"
          />
          Also enable bulk ordering on approval
        </label>
      )}

      <div className="grid grid-cols-2 gap-2">
        {awaitingDecision && canApprove && (
          <>
            <Button variant="success" onClick={onApprove} disabled={busy}>
              {busy ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <ShieldCheck className="size-4" aria-hidden="true" />
              )}
              Approve
            </Button>
            <Button variant="danger" onClick={onReject} disabled={busy}>
              <ShieldX className="size-4" aria-hidden="true" />
              Reject
            </Button>
          </>
        )}

        {awaitingDecision && canVerify && (
          <Button variant="outline" className="col-span-2" onClick={onRequestDocuments} disabled={busy}>
            <FileText className="size-4" aria-hidden="true" />
            Request more documents
          </Button>
        )}

        {isVerified && canApprove && (
          <Button
            variant={canOrder ? 'outline' : 'primary'}
            className="col-span-2"
            onClick={() => onSetOrdering(!canOrder)}
            disabled={busy}
          >
            {canOrder ? 'Disable bulk ordering' : 'Enable bulk ordering'}
          </Button>
        )}

        {canSuspend &&
          (suspended ? (
            <Button variant="outline" className="col-span-2" onClick={onReinstate} disabled={busy}>
              <RotateCcw className="size-4" aria-hidden="true" />
              Reinstate account
            </Button>
          ) : (
            <Button variant="danger" className="col-span-2" onClick={onSuspend} disabled={busy}>
              <Ban className="size-4" aria-hidden="true" />
              Suspend account
            </Button>
          ))}
      </div>
    </div>
  );
}
